/**
 * Competitor Comparison Page
 *
 * Tabs:
 * 1. URL Comparison — compare SEO scores across 2-5 URLs side-by-side
 * 2. Link Policy   — manage Never Use / Do Use domain lists
 */

import { useState, useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	Plus,
	X,
	Loader2,
	BarChart3,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	Globe,
	Image,
	Heading1,
	FileText,
	Scale,
	CheckCircle,
	TrendingUp,
	Search,
	ShieldX,
	ShieldCheck,
	Trash2,
	Link2,
	TriangleAlert,
	RefreshCw,
	Telescope,
	ChevronsUpDown,
	ExternalLink,
	Check,
	MapPin,
	Bookmark,
	RotateCcw,
	Square,
	CheckSquare,
	FileEdit,
	CircleDot,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CompetitorTopPages } from "@/components/competitor/CompetitorTopPages";
import { FEATURE_FLAGS } from "@/config/environment";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { apiClient } from "@/services/apiClient";
import { cn } from "@/lib/utils";
import { useTenantStore } from "@/stores/useTenantStore";
import { useBrandBible } from "@/hooks/useBrandBible";
import {
	useLinkPolicy,
	useAddToPolicy,
	useRemoveFromPolicy,
	useSavedGaps,
	useDeleteGap,
	useEnrichGaps,
	type SavedGap,
	type GapContentStatus,
} from "@/hooks/useCompetitorTools";
import type { LinkPolicyEntry, SERPAnalysisResult } from "@/services/competitorService";
import { contentPlannerService } from "@/services/contentPlannerService";
import type { ClusterSummary } from "@/types/contentPlanner";
import { useContentList } from "@/hooks/useBlogCreationAPI";
import { useLocationOnlyPages } from "@/hooks/useLocationOnlyPages";
import { useServiceOnlyPages } from "@/hooks/useServiceOnlyPages";
import { useLocationServicePages } from "@/hooks/useLocationServicePages";
import { INTENT_LABELS, INTENT_FULL_LABELS, getDifficultyLevel, DIFFICULTY_LABELS, FRUIT_TIER_LABELS, type FruitTier } from "@/types/contentPlanner";

// ---------------------------------------------------------------------------
// Types matching backend CompareResponse
// ---------------------------------------------------------------------------

interface PageSpeedScores {
	performance: number | null;
	accessibility: number | null;
	best_practices: number | null;
	seo: number | null;
	fcp: number | null;
	lcp: number | null;
	cls: number | null;
	tbt: number | null;
}

interface MetaInfo {
	title: string | null;
	title_length: number;
	meta_description: string | null;
	meta_length: number;
	has_og_tags: boolean;
	has_twitter_tags: boolean;
	favicon: string | null;
}

interface HeadingInfo {
	h1_count: number;
	h1_texts: string[];
	has_h1: boolean;
}

interface CompareUrlResult {
	url: string;
	meta: MetaInfo;
	headings: HeadingInfo;
	pagespeed_mobile: PageSpeedScores;
	pagespeed_desktop: PageSpeedScores;
	image_alt_coverage: number | null;
	total_images: number;
	images_with_alt: number;
	error: string | null;
}

interface CompareResponse {
	results: CompareUrlResult[];
}

// ---------------------------------------------------------------------------
// Internal link types + gap detection
// ---------------------------------------------------------------------------

interface InternalLink {
	id: string;
	title: string;
	keyword: string;
	type: "blog" | "location" | "service" | "combined";
	editPath: string;
}

/** True if a SERP topic and an internal item share meaningful keywords */
function topicMatchesContent(topic: string, item: InternalLink): boolean {
	const t = topic.toLowerCase();
	const fields = [item.title, item.keyword].filter(Boolean).map((s) => s.toLowerCase());
	return fields.some((f) => f.includes(t) || t.includes(f));
}

// ---------------------------------------------------------------------------
// Score helpers
// ---------------------------------------------------------------------------

function getScoreColor(score: number | null): string {
	if (score === null) return "text-muted-foreground";
	if (score >= 90) return "text-[hsl(var(--status-success))]";
	if (score >= 50) return "text-amber-600 dark:text-amber-400";
	return "text-destructive";
}

function getScoreBg(score: number | null): string {
	if (score === null) return "bg-muted";
	if (score >= 90) return "bg-[hsl(var(--status-success-bg))]";
	if (score >= 50) return "bg-amber-100 dark:bg-amber-900/40";
	return "bg-destructive/5";
}

const SCORE_TOOLTIPS: Record<string, string> = {
	"Perf": "Performance — page load speed (FCP, LCP, CLS, TBT)",
	"SEO": "SEO — technical signals Google uses for ranking",
	"A11y": "Accessibility — usability for people with disabilities",
	"B.P.": "Best Practices — modern web security & code quality",
};

function ScoreCircle({ score, label }: { score: number | null; label: string }) {
	const color = getScoreColor(score);
	const bg = getScoreBg(score);
	const tip = SCORE_TOOLTIPS[label];

	return (
		<TooltipProvider delayDuration={300}>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="flex flex-col items-center gap-1 cursor-default">
						<div
							className={cn(
								"w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold border-2",
								bg,
								score === null
									? "border-muted-foreground/20"
									: score >= 90
										? "border-[hsl(var(--status-success))]"
										: score >= 50
											? "border-amber-400"
											: "border-destructive",
							)}
						>
							<span className={color}>{score ?? "--"}</span>
						</div>
						<span className="text-xs text-muted-foreground text-center leading-tight">
							{label}
						</span>
					</div>
				</TooltipTrigger>
				{tip && (
					<TooltipContent side="top" className="max-w-[200px] text-xs text-center">
						{tip}
					</TooltipContent>
				)}
			</Tooltip>
		</TooltipProvider>
	);
}

function MetricRow({
	label,
	values,
	format,
	higherIsBetter = true,
}: {
	label: string;
	values: (number | null)[];
	format?: (v: number) => string;
	higherIsBetter?: boolean;
}) {
	const numericValues = values.filter((v): v is number => v !== null);
	const bestValue =
		numericValues.length > 0
			? higherIsBetter
				? Math.max(...numericValues)
				: Math.min(...numericValues)
			: null;

	return (
		<div className="grid items-center gap-3" style={{ gridTemplateColumns: `140px repeat(${values.length}, 1fr)` }}>
			<span className="text-sm text-muted-foreground">{label}</span>
			{values.map((v, i) => {
				const isBest = v !== null && v === bestValue && numericValues.length > 1;
				return (
					<span
						key={i}
						className={cn(
							"text-sm font-medium text-center",
							v === null
								? "text-muted-foreground"
								: isBest
									? "text-[hsl(var(--status-success))] font-semibold"
									: "",
						)}
					>
						{v === null ? "--" : format ? format(v) : v}
						{isBest && numericValues.length > 1 && (
							<CheckCircle2 className="w-3 h-3 inline ml-1 text-[hsl(var(--status-success))]" />
						)}
					</span>
				);
			})}
		</div>
	);
}

function BooleanRow({
	label,
	values,
}: {
	label: string;
	values: boolean[];
}) {
	return (
		<div className="grid items-center gap-3" style={{ gridTemplateColumns: `140px repeat(${values.length}, 1fr)` }}>
			<span className="text-sm text-muted-foreground">{label}</span>
			{values.map((v, i) => (
				<span key={i} className="flex justify-center">
					{v ? (
						<CheckCircle2 className="w-4 h-4 text-teal" />
					) : (
						<XCircle className="w-4 h-4 text-destructive" />
					)}
				</span>
			))}
		</div>
	);
}

function getDomain(url: string): string {
	try {
		return new URL(url).hostname;
	} catch {
		return url;
	}
}

// ---------------------------------------------------------------------------
// Perfect For Card
// ---------------------------------------------------------------------------

function PerfectForCard({
	icon: Icon,
	title,
	description,
	iconColor,
}: {
	icon: React.ElementType;
	title: string;
	description: string;
	iconColor: string;
}) {
	return (
		<div className="flex items-start gap-3 p-4 bg-muted/80 rounded-lg border border-border">
			<div className={cn("mt-0.5", iconColor)}>
				<Icon className="w-5 h-5" />
			</div>
			<div>
				<h4 className="font-semibold text-foreground text-sm">{title}</h4>
				<p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Link Policy Panel (single list: never_use or do_use)
// ---------------------------------------------------------------------------

function getDomainFromUrl(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return url;
	}
}

// ---------------------------------------------------------------------------
// PolicyListPanel — manual add + scrollable domain list (no research here)
// ---------------------------------------------------------------------------

function PolicyListPanel({
	tenantId,
	policyType,
	externalDomainToAdd,
	onExternalAdded,
}: {
	tenantId: string;
	policyType: "never_use" | "do_use";
	externalDomainToAdd?: { domain: string; title: string } | null;
	onExternalAdded?: () => void;
}) {
	const [inputDomain, setInputDomain] = useState("");
	const [inputName, setInputName] = useState("");
	const [conflictInfo, setConflictInfo] = useState<LinkPolicyEntry | null>(null);

	const { entries, isLoading, isFetching, refetch } = useLinkPolicy(tenantId, policyType);
	const addMutation = useAddToPolicy();
	const removeMutation = useRemoveFromPolicy();

	const isNever = policyType === "never_use";
	const label = isNever ? "Never Use" : "Do Use";

	// Handle domain pushed in from the shared Research panel
	const [lastExternal, setLastExternal] = useState<string | null>(null);
	if (externalDomainToAdd && externalDomainToAdd.domain !== lastExternal) {
		setLastExternal(externalDomainToAdd.domain);
		// Fire and forget — errors handled inside handleAdd
		handleAddDomain(externalDomainToAdd.domain, externalDomainToAdd.title);
	}

	async function handleAddDomain(domain: string, name?: string) {
		const d = domain.trim();
		if (!d) return;
		setConflictInfo(null);
		try {
			await addMutation.mutateAsync({ tenantId, domain: d, policyType, name });
			if (d === inputDomain.trim()) { setInputDomain(""); setInputName(""); }
			onExternalAdded?.();
			toast.success(`Added "${d}" to ${label} list`);
		} catch (err: any) {
			if (err?.response?.status === 409) {
				const existing = err.response?.data?.existing_entry as LinkPolicyEntry | undefined;
				if (existing && d === inputDomain.trim()) setConflictInfo(existing);
				toast.error(err?.response?.data?.detail || `"${d}" already exists`);
			} else {
				toast.error(err?.response?.data?.detail || "Failed to add domain");
			}
		}
	}

	const handleRemove = async (entry: LinkPolicyEntry) => {
		try {
			await removeMutation.mutateAsync({ tenantId, entryId: entry.id });
			toast.success(`Removed "${entry.domain}"`);
		} catch {
			toast.error("Failed to remove domain");
		}
	};

	return (
		<div className="space-y-3">
			{/* Manual add */}
			<div className="flex gap-2">
				<Input
					placeholder="domain.com"
					value={inputDomain}
					onChange={(e) => { setInputDomain(e.target.value); setConflictInfo(null); }}
					onKeyDown={(e) => e.key === "Enter" && handleAddDomain(inputDomain, inputName || undefined)}
					className="h-9 text-sm"
				/>
				<Input
					placeholder="Label (optional)"
					value={inputName}
					onChange={(e) => setInputName(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleAddDomain(inputDomain, inputName || undefined)}
					className="h-9 text-sm w-32"
				/>
				<Button
					onClick={() => handleAddDomain(inputDomain, inputName || undefined)}
					disabled={!inputDomain.trim() || addMutation.isPending}
					size="sm"
					variant="outline"
					className="h-9 px-3 shrink-0"
				>
					{addMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
				</Button>
			</div>

			{conflictInfo && (
				<div className="flex items-start gap-2 p-2.5 rounded-md bg-coral-light border border-coral/30 text-sm">
					<TriangleAlert className="w-4 h-4 text-coral shrink-0 mt-0.5" />
					<span className="text-coral-dark">
						<strong>{conflictInfo.domain}</strong> is already in the{" "}
						<strong>{conflictInfo.policy_type === "never_use" ? "Never Use" : "Do Use"}</strong> list.
					</span>
				</div>
			)}

			{/* Count + refresh */}
			<div className="flex items-center justify-between">
				<span className="text-xs text-muted-foreground">{entries.length} domain{entries.length !== 1 ? "s" : ""}</span>
				<Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching} className="h-6 px-2 text-xs text-muted-foreground">
					<RefreshCw className={cn("w-3 h-3 mr-1", isFetching && "animate-spin")} />
					Refresh
				</Button>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-6 text-muted-foreground">
					<Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
				</div>
			) : entries.length === 0 ? (
				<div className="text-center py-6 text-sm text-muted-foreground">No domains yet.</div>
			) : (
				<ScrollArea className="h-72 pr-1"><div className="space-y-1.5">
					{entries.map((entry) => (
						<div key={entry.id} className="flex items-center gap-3 px-3 py-2 rounded-md border bg-background hover:bg-muted/30 transition-colors group">
							<Link2 className={cn("w-3.5 h-3.5 shrink-0", isNever ? "text-destructive" : "text-teal")} />
							<span className="text-sm font-medium flex-1 truncate">{entry.domain}</span>
							{entry.name && entry.name !== `${entry.domain} (blocked)` && (
								<span className="text-xs text-muted-foreground truncate max-w-[100px]">{entry.name}</span>
							)}
							<Button
								variant="ghost" size="icon"
								onClick={() => handleRemove(entry)}
								disabled={removeMutation.isPending}
								className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
							>
								<Trash2 className="w-3.5 h-3.5" />
							</Button>
						</div>
					))}
				</div></ScrollArea>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Shared Research Panel — loads cluster cards, runs SERP, shows results
// ---------------------------------------------------------------------------

// Graded result types
interface NeverUseSuggestion {
	domain: string;
	title: string;
	position: number;       // 1-based SERP rank
	word_count: number;
	avg_word_count: number;
	main_topics: string[];
	threat_score: number;   // 0–100
	threat_level: "High" | "Medium" | "Low";
	reasons: string[];
}


interface DoUseSuggestion {
	domain: string;
	title: string;
	authority_tier: "gov" | "edu" | "research" | "reference" | "news" | "industry";
	authority_score: number; // 65–95
	topic_match: number;     // overlapping topics count
	reasons: string[];
}

// Authority domain classification — mirrors quality_report_service.py tiers
const AUTHORITY_PATTERNS: Array<{
	tier: DoUseSuggestion["authority_tier"];
	score: number;
	tlds: string[];
	domains: string[];
}> = [
	{
		tier: "gov",
		score: 95,
		tlds: [".gov", ".gov.au", ".gov.uk", ".gov.ca", ".gov.nz"],
		domains: [],
	},
	{
		tier: "edu",
		score: 90,
		tlds: [".edu", ".ac.uk", ".edu.au"],
		domains: [],
	},
	{
		tier: "research",
		score: 85,
		tlds: [],
		domains: [
			"pubmed.ncbi.nlm.nih.gov", "ncbi.nlm.nih.gov", "arxiv.org",
			"nature.com", "doi.org", "nih.gov", "springer.com",
			"sciencedirect.com", "researchgate.net", "jstor.org",
			"plos.org", "frontiersin.org",
		],
	},
	{
		tier: "reference",
		score: 70,
		tlds: [],
		domains: [
			"wikipedia.org", "britannica.com", "merriam-webster.com",
			"dictionary.com", "investopedia.com",
		],
	},
	{
		tier: "news",
		score: 70,
		tlds: [],
		domains: [
			"nytimes.com", "bbc.com", "bbc.co.uk", "reuters.com",
			"bloomberg.com", "wsj.com", "cnn.com", "forbes.com",
			"apnews.com", "theguardian.com", "washingtonpost.com",
			"theatlantic.com", "wired.com", "techcrunch.com", "engadget.com",
		],
	},
	{
		tier: "industry",
		score: 65,
		tlds: [],
		domains: [
			"ashrae.org", "ieee.org", "osha.gov", "epa.gov", "energy.gov",
			"nfpa.org", "hud.gov", "energystar.gov", "consumerreports.org",
			"angi.com", "homeadvisor.com", "angieslist.com", "houzz.com",
			"bobvila.com", "thisoldhouse.com",
		],
	},
];

function classifyDomain(domain: string): DoUseSuggestion["authority_tier"] | null {
	for (const pattern of AUTHORITY_PATTERNS) {
		for (const tld of pattern.tlds) {
			if (domain.endsWith(tld)) return pattern.tier;
		}
		for (const d of pattern.domains) {
			if (domain === d || domain.endsWith("." + d)) return pattern.tier;
		}
	}
	return null;
}

function getTierScore(tier: DoUseSuggestion["authority_tier"]): number {
	return AUTHORITY_PATTERNS.find((p) => p.tier === tier)?.score ?? 65;
}

function buildNeverSuggestion(
	domain: string,
	title: string,
	position: number,
	totalPositions: number,
	wordCount: number,
	avgWordCount: number,
	mainTopics: string[],
): NeverUseSuggestion {
	const positionScore = Math.round(((totalPositions - position) / totalPositions) * 35);
	const wcRatio = wordCount / Math.max(avgWordCount, 1);
	const wordCountScore = wcRatio > 1.15 ? 35 : wcRatio > 0.85 ? 20 : 10;
	const topicScore = Math.min(mainTopics.length * 10, 30);
	const threat_score = positionScore + wordCountScore + topicScore;
	const threat_level: NeverUseSuggestion["threat_level"] =
		threat_score >= 65 ? "High" : threat_score >= 35 ? "Medium" : "Low";

	const reasons: string[] = [`Ranked #${position + 1} in SERP`];
	if (wcRatio > 1.15) reasons.push(`Long-form content (${wordCount.toLocaleString()} words)`);
	else if (wcRatio > 0.85) reasons.push(`Similar word count (${wordCount.toLocaleString()} words)`);
	if (mainTopics.length > 0) reasons.push(`${mainTopics.length} shared topic${mainTopics.length !== 1 ? "s" : ""}`);

	return { domain, title, position, word_count: wordCount, avg_word_count: avgWordCount, main_topics: mainTopics, threat_score, threat_level, reasons };
}

const TIER_LABELS: Record<DoUseSuggestion["authority_tier"], string> = {
	gov: "Government",
	edu: "Education",
	research: "Research",
	reference: "Reference",
	news: "News",
	industry: "Industry",
};

// Authority tier colors — uses project tokens where possible;
// gov/edu/news/industry use toned-down semantic variants (no project token exists for these)
const TIER_COLORS: Record<DoUseSuggestion["authority_tier"], string> = {
	gov: "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border border-blue-100",
	edu: "bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-400 border border-violet-100",
	research: "bg-teal-light text-teal-dark",
	reference: "bg-muted text-muted-foreground",
	news: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-100",
	industry: "bg-coral-light text-coral-dark",
};

const THREAT_COLORS: Record<NeverUseSuggestion["threat_level"], string> = {
	High: "bg-destructive/10 text-destructive",
	Medium: "bg-coral-light text-coral-dark",
	Low: "bg-muted text-muted-foreground",
};

const INTENT_BG: Record<string, string> = {
	informational: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400",
	commercial_investigation: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400",
	transactional: "bg-teal-light text-teal-dark",
	navigational: "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-400",
};

function difficultyColor(kd: number): string {
	if (kd <= 30) return "text-[hsl(var(--status-success))]";
	if (kd <= 50) return "text-amber-600 dark:text-amber-400";
	if (kd <= 70) return "text-orange-600 dark:text-orange-400";
	return "text-destructive";
}

// Fruit tier badge colors using project tokens (low = easy = teal, high = hard = coral/destructive)
const FRUIT_TIER_TOKEN_COLORS: Record<FruitTier, string> = {
	low: "bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]",
	mid: "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400",
	high: "bg-destructive/10 text-destructive",
};

// ---------------------------------------------------------------------------
// Your Site column sub-component
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<InternalLink["type"], string> = {
	blog: "Blog",
	location: "Location",
	service: "Service",
	combined: "Page",
};

function YourSiteRows({
	allTopics,
	internalLinks,
	savedGaps,
	onSaveGap,
}: {
	allTopics: string[];
	internalLinks: InternalLink[];
	savedGaps: Set<string>;
	onSaveGap: (topic: string) => Promise<void>;
}) {
	const [savingTopic, setSavingTopic] = useState<string | null>(null);

	if (allTopics.length === 0) {
		return (
			<p className="text-xs text-muted-foreground py-3 text-center">
				No topics found — try a different keyword.
			</p>
		);
	}

	const rows = allTopics.map((topic) => ({
		topic,
		match: internalLinks.find((item) => topicMatchesContent(topic, item)),
	}));
	const gapCount = rows.filter((r) => !r.match).length;

	const handleSave = async (topic: string) => {
		setSavingTopic(topic);
		try { await onSaveGap(topic); }
		finally { setSavingTopic(null); }
	};

	return (
		<>
			{gapCount > 0 && (
				<p className="text-[10px] text-muted-foreground mb-1.5">
					<span className="text-coral font-semibold">{gapCount} gap{gapCount !== 1 ? "s" : ""}</span>
					{" · "}{rows.length - gapCount} covered
				</p>
			)}
			{rows.map(({ topic, match }) => (
				<div key={topic} className="rounded-md border bg-background px-3 py-2 space-y-1">
					<div className="flex items-start gap-2">
						<span className="flex-1 text-[11px] text-muted-foreground leading-snug capitalize">{topic}</span>
						{match ? (
							<Badge variant="secondary" className="text-[10px] shrink-0 bg-teal-light text-teal-dark border-teal/30">
								{TYPE_LABELS[match.type]}
							</Badge>
						) : savedGaps.has(topic) ? (
							<Badge variant="secondary" className="text-[10px] shrink-0 bg-teal-light text-teal-dark border-teal/30">
								Saved ✓
							</Badge>
						) : (
							<Button
								size="sm" variant="outline"
								onClick={() => handleSave(topic)}
								disabled={savingTopic === topic}
								className="h-5 px-1.5 text-[10px] border-coral/40 text-coral hover:bg-coral-light shrink-0"
							>
								{savingTopic === topic ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : "+ Plan"}
							</Button>
						)}
					</div>
					{match && (
						<p className="text-[10px] text-muted-foreground truncate">{match.title}</p>
					)}
				</div>
			))}
		</>
	);
}

// Group clusters by their topical map
interface MapGroup { mapId: string; mapName: string; clusters: ClusterSummary[]; }

function ResearchPanel({
	tenantId,
	onAdd,
	onNavigateToGaps,
}: {
	tenantId: string;
	onAdd: (domain: string, title: string, policyType: "never_use" | "do_use") => void;
	onNavigateToGaps?: () => void;
}) {
	const navigate = useNavigate();
	const [mapGroups, setMapGroups] = useState<MapGroup[]>([]);
	const [loadingClusters, setLoadingClusters] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [comboOpen, setComboOpen] = useState(false);
	const [customKeyword, setCustomKeyword] = useState("");
	const [searchProgress, setSearchProgress] = useState<{ done: number; total: number } | null>(null);
	const [isSearching, setIsSearching] = useState(false);
	const [neverSuggestions, setNeverSuggestions] = useState<NeverUseSuggestion[]>([]);
	const [doSuggestions, setDoSuggestions] = useState<DoUseSuggestion[]>([]);
	const [allTopics, setAllTopics] = useState<string[]>([]);
	const [savedGapKeywords, setSavedGapKeywords] = useState<Set<string>>(new Set());
	const [isSavingAllGaps, setIsSavingAllGaps] = useState(false);
	const [searchLocation, setSearchLocation] = useState("");
	const [lastSearchLocation, setLastSearchLocation] = useState("");
	const [addingMap, setAddingMap] = useState<Record<string, boolean>>({});
	const [isBulkAdding, setIsBulkAdding] = useState<"never" | "do" | "both" | null>(null);
	const [hideAdded, setHideAdded] = useState(true);

	// Auto-fill location from brand bible
	const { data: brandBible } = useBrandBible(tenantId);
	const [locationFromBrandBible, setLocationFromBrandBible] = useState(false);
	useEffect(() => {
		if (!brandBible || searchLocation) return;
		const loc =
			(brandBible.locations ? Object.keys(brandBible.locations)[0] : brandBible.service_locations?.[0]) ||
			[brandBible.locality, brandBible.region].filter(Boolean).join(", ");
		if (loc) {
			setSearchLocation(loc);
			setLocationFromBrandBible(true);
		}
	}, [brandBible]);
	const neverEntries = useLinkPolicy(tenantId, "never_use");
	const doEntries = useLinkPolicy(tenantId, "do_use");

	// Internal content — all three page types + blog posts
	const { data: blogData } = useContentList();
	const { data: locationPages } = useLocationOnlyPages(tenantId);
	const { data: servicePages } = useServiceOnlyPages(tenantId);
	const { data: combinedPages } = useLocationServicePages(tenantId);

	// Normalize all internal content into a unified list — memoized to avoid rebuild on every render
	const internalLinks: InternalLink[] = useMemo(() => [
		...(blogData?.items ?? []).map((p) => ({
			id: p.id,
			title: p.title || p.keyword,
			keyword: p.keyword,
			type: "blog" as const,
			editPath: `/editor/blog/${encodeURIComponent(p.id)}`,
		})),
		...(Array.isArray(locationPages) ? locationPages : []).map((p) => ({
			id: p.id,
			title: p.seo_title || p.location_name,
			keyword: p.target_keyword || p.location_name,
			type: "location" as const,
			editPath: `/pages/location-only`,
		})),
		...(Array.isArray(servicePages) ? servicePages : []).map((p) => ({
			id: p.id,
			title: p.seo_title || p.service_name,
			keyword: p.target_keyword || p.service_name,
			type: "service" as const,
			editPath: `/pages/service-only`,
		})),
		...(Array.isArray(combinedPages) ? combinedPages : []).map((p) => ({
			id: p.id,
			title: p.seo_title || `${p.service_name} — ${p.location_name}`,
			keyword: p.target_keyword || p.service_name,
			type: "combined" as const,
			editPath: `/pages/location-service`,
		})),
	], [blogData, locationPages, servicePages, combinedPages]);

	const loadClusters = async () => {
		setLoadingClusters(true);
		try {
			const resp = await contentPlannerService.listMaps({ status: "completed", limit: 20 });
			const maps = resp.maps ?? [];
			const groups: MapGroup[] = [];
			for (const map of maps) {
				try {
					const cs = await contentPlannerService.listClusters(map.map_id, { limit: 50 });
					if (cs.length > 0) {
						groups.push({
							mapId: map.map_id,
							mapName: map.seed_keyword,
							clusters: cs,
						});
					}
				} catch { /* skip failed map */ }
			}
			setMapGroups(groups);
			// Auto-select first cluster if nothing selected yet
			if (groups.length > 0 && groups[0].clusters.length > 0) {
				setSelectedIds((prev) => prev.size === 0 ? new Set([groups[0].clusters[0].id]) : prev);
			}
		} catch { /* silently fail */ } finally {
			setLoadingClusters(false);
		}
	};

	useEffect(() => { loadClusters(); }, []);

	const allClusters = mapGroups.flatMap((g) => g.clusters);
	const selectedClusters = allClusters.filter((c) => selectedIds.has(c.id));
	const allSelected = allClusters.length > 0 && selectedIds.size === allClusters.length;
	const customKw = customKeyword.trim();
	const hasSelection = customKw || selectedIds.size > 0;

	const toggleCluster = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		setSelectedIds(allSelected ? new Set() : new Set(allClusters.map((c) => c.id)));
	};

	const handleSearch = async () => {
		if (!hasSelection) return;
		setIsSearching(true);
		setNeverSuggestions([]);
		setDoSuggestions([]);
		setAllTopics([]);
		setSavedGapKeywords(new Set());
		setLastSearchLocation(searchLocation.trim());

		const keywords = customKw ? [customKw] : selectedClusters.map((c) => c.name);
		setSearchProgress({ done: 0, total: keywords.length });

		const seenNever = new Set<string>();
		const seenDo = new Set<string>();
		const seenTopics = new Set<string>();
		const accNever: NeverUseSuggestion[] = [];
		const accDo: DoUseSuggestion[] = [];
		let skippedKeywords = 0;

		// Process up to 3 keywords in parallel for speed
		const CONCURRENCY = 3;
		const processKw = async (kw: string): Promise<SERPAnalysisResult> => {
			const { data } = await apiClient.post<SERPAnalysisResult>("/content/serp-analyze", {
				keyword: kw,
				tenant_id: tenantId,
				...(searchLocation.trim() ? { location: searchLocation.trim() } : {}),
			});
			return data;
		};

		let done = 0;
		for (let i = 0; i < keywords.length; i += CONCURRENCY) {
			const batch = keywords.slice(i, i + CONCURRENCY);
			const settled = await Promise.allSettled(batch.map(processKw));

			settled.forEach((result) => {
				if (result.status === "rejected") { skippedKeywords++; return; }
				const data = result.value;
				const competitors = data.competitors ?? [];
				const avg = data.average_word_count || 0;
				const total = competitors.length;

				// Primary: aggregated common_topics from SERP analysis
				(data.common_topics ?? []).forEach((t) => seenTopics.add(t.toLowerCase()));

				competitors.forEach((c, idx) => {
					(c.main_topics ?? []).forEach((t) => seenTopics.add(t.toLowerCase()));

					// H2 headings are the richest gap signal — actual article sections from competitors
					const skipHeadings = /^(about|contact|faq|menu|navigation|footer|header|sidebar|subscribe|newsletter|comments?|share|related|tags?|categor)/i;
					(c.h2_headings ?? []).forEach((h) => {
						const cleaned = h.replace(/^\d+[\.\)]\s*/, "").trim(); // strip "1. " prefixes
						const wordCount = cleaned.split(/\s+/).length;
						if (wordCount >= 3 && wordCount <= 10 && cleaned.length < 90 && !skipHeadings.test(cleaned)) {
							seenTopics.add(cleaned.toLowerCase());
						}
					});
					const domain = getDomainFromUrl(c.url);
					if (!domain) return;
					const tier = classifyDomain(domain);
					if (tier) {
						if (!seenDo.has(domain)) {
							seenDo.add(domain);
							const tierScore = getTierScore(tier);
							const topicMatch = (c.main_topics ?? []).length;
							const reasons: string[] = [TIER_LABELS[tier] + " domain"];
							if (topicMatch > 0) reasons.push(`${topicMatch} relevant topic${topicMatch !== 1 ? "s" : ""}`);
							accDo.push({ domain, title: c.title || domain, authority_tier: tier, authority_score: tierScore, topic_match: topicMatch, reasons });
						}
					} else {
						if (!seenNever.has(domain)) {
							seenNever.add(domain);
							accNever.push(buildNeverSuggestion(domain, c.title || domain, idx, total, c.word_count || 0, avg, c.main_topics ?? []));
						}
					}
				});
			});

			done += batch.length;
			setSearchProgress({ done: Math.min(done, keywords.length), total: keywords.length });
		}

		// Sort: Never Use by threat_score desc, Do Use by authority_score desc
		accNever.sort((a, b) => b.threat_score - a.threat_score);
		accDo.sort((a, b) => b.authority_score - a.authority_score);

		setNeverSuggestions(accNever);
		setDoSuggestions(accDo);
		setAllTopics(Array.from(seenTopics));
		setSearchProgress(null);
		setIsSearching(false);
		if (skippedKeywords > 0) toast.warning(`${skippedKeywords} keyword${skippedKeywords !== 1 ? "s" : ""} skipped due to errors`);
		if (accNever.length === 0 && accDo.length === 0) toast.info("No results found");
	};

	const handleSaveGap = async (topic: string) => {
		try {
			await apiClient.post(
				"/content-planner/gaps",
				{
					keywords: [topic],
					serp_never_use_domains: neverSuggestions.map((s) => s.domain),
					serp_do_use_domains: doSuggestions.map((s) => s.domain),
				},
				{ params: { tenant_id: tenantId } },
			);
			setSavedGapKeywords((prev) => new Set([...prev, topic]));
			toast.success(`"${topic}" saved to gaps`, {
				action: { label: "View Saved Gaps →", onClick: () => onNavigateToGaps?.() },
			});
		} catch {
			toast.error(`Failed to save "${topic}"`);
		}
	};

	const handleSaveAllGaps = async () => {
		const unsaved = internalLinks
			? allTopics.filter((t) => !savedGapKeywords.has(t) && !internalLinks.some((item) => topicMatchesContent(t, item)))
			: allTopics.filter((t) => !savedGapKeywords.has(t));
		if (unsaved.length === 0) return;
		setIsSavingAllGaps(true);
		try {
			await apiClient.post(
				"/content-planner/gaps",
				{
					keywords: unsaved,
					serp_never_use_domains: neverSuggestions.map((s) => s.domain),
					serp_do_use_domains: doSuggestions.map((s) => s.domain),
				},
				{ params: { tenant_id: tenantId } },
			);
			setSavedGapKeywords((prev) => new Set([...prev, ...unsaved]));
			toast.success(`${unsaved.length} gap${unsaved.length !== 1 ? "s" : ""} saved`, {
				action: { label: "View Saved Gaps →", onClick: () => onNavigateToGaps?.() },
				duration: 6000,
			});
		} catch {
			toast.error("Failed to save gaps to Content Planner");
		} finally {
			setIsSavingAllGaps(false);
		}
	};

	const handleAdd = async (domain: string, title: string, policyType: "never_use" | "do_use") => {
		const key = domain + policyType;
		setAddingMap((prev) => ({ ...prev, [key]: true }));
		try { await onAdd(domain, title, policyType); }
		finally {
			setAddingMap((prev) => { const n = { ...prev }; delete n[key]; return n; });
		}
	};

	const handleBulkAdd = async (mode: "never" | "do" | "both") => {
		setIsBulkAdding(mode);
		try {
			const tasks: Promise<void>[] = [];
			if (mode === "never" || mode === "both") {
				for (const s of neverSuggestions) {
					const inNever = neverEntries.entries.some((e) => e.domain === s.domain);
					if (!inNever) tasks.push(onAdd(s.domain, s.title, "never_use").catch(() => {}));
				}
			}
			if (mode === "do" || mode === "both") {
				for (const s of doSuggestions) {
					const inDo = doEntries.entries.some((e) => e.domain === s.domain);
					if (!inDo) tasks.push(onAdd(s.domain, s.title, "do_use").catch(() => {}));
				}
			}
			await Promise.all(tasks);
			const labels = mode === "both" ? "Never Use + Do Use lists" : mode === "never" ? "Never Use list" : "Do Use list";
			toast.success(`All domains added to ${labels}`);
		} finally {
		  setIsBulkAdding(null);
		}
	};

	return (
		<Card className="border shadow-sm mb-6">
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-base">
						<Telescope className="w-4 h-4 text-coral" />
						Research Competitors
					</CardTitle>
					<div className="flex items-center gap-2">
						<Button
							variant="ghost"
							size="sm"
							onClick={loadClusters}
							disabled={loadingClusters}
							className="h-6 px-2 text-xs text-muted-foreground"
						>
							<RefreshCw className={cn("w-3 h-3 mr-1", loadingClusters && "animate-spin")} />
							Refresh
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => navigate("/seo/keyword-research")}
							className="h-6 px-2 text-xs text-muted-foreground"
						>
							<ExternalLink className="w-3 h-3 mr-1" />
							Research keywords
						</Button>
					</div>
				</div>
				<p className="text-xs text-muted-foreground">
					Pick a cluster from your keyword research, then find competitors to classify.
				</p>
			</CardHeader>
			<CardContent className="space-y-4">

				{/* Cluster multi-select combobox */}
				<div className="space-y-1.5">
					<p className="text-xs font-medium text-muted-foreground">Keyword clusters</p>
					<Popover open={comboOpen} onOpenChange={setComboOpen}>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								role="combobox"
								aria-expanded={comboOpen}
								disabled={loadingClusters}
								className="w-full justify-between h-9 text-sm font-normal"
							>
								{loadingClusters ? (
									<span className="flex items-center gap-2 text-muted-foreground">
										<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading clusters…
									</span>
								) : customKw ? (
									<span className="text-foreground truncate">Custom: "{customKw}"</span>
								) : selectedIds.size === 0 ? (
									<span className="text-muted-foreground">Select keyword clusters…</span>
								) : allSelected ? (
									<span className="text-foreground">All clusters ({allClusters.length})</span>
								) : selectedIds.size === 1 ? (
									<span className="flex items-center gap-2 truncate">
										<span className="truncate">{selectedClusters[0]?.name}</span>
										<span className={cn("shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold", INTENT_BG[selectedClusters[0]?.primary_intent] ?? "bg-muted")}>
											{INTENT_LABELS[selectedClusters[0]?.primary_intent]}
										</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{(selectedClusters[0]?.total_volume ?? 0).toLocaleString()} vol
										</span>
									</span>
								) : (
									<span className="text-foreground">{selectedIds.size} clusters selected</span>
								)}
								<ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-[520px] p-0" align="start">
							<Command>
								<CommandInput placeholder="Search clusters…" className="h-9" />
								<CommandList className="max-h-80">
									<CommandEmpty>No clusters found.</CommandEmpty>
									{allClusters.length > 0 && (
										<CommandGroup>
											<CommandItem
												value="__select_all__"
												onSelect={toggleAll}
												className="flex items-center gap-2 py-2 font-medium border-b"
											>
												<Check className={cn("w-3.5 h-3.5 shrink-0", allSelected ? "opacity-100" : "opacity-0")} />
												<span className="flex-1 text-sm">Select all clusters</span>
												<span className="text-xs text-muted-foreground">{allClusters.length} total</span>
											</CommandItem>
										</CommandGroup>
									)}
									{mapGroups.map((group, gi) => (
										<>
											{gi > 0 && <CommandSeparator key={`sep-${group.mapId}`} />}
											<CommandGroup key={group.mapId} heading={group.mapName}>
												{group.clusters.map((c) => {
													const isSelected = selectedIds.has(c.id);
													const diffLevel = getDifficultyLevel(c.avg_difficulty);
													const intentBg = INTENT_BG[c.primary_intent] ?? "bg-muted text-muted-foreground";
													return (
														<CommandItem
															key={c.id}
															value={`${group.mapName} ${c.name} ${c.keywords_preview?.join(" ") ?? ""}`}
															onSelect={() => { toggleCluster(c.id); setCustomKeyword(""); }}
															className="flex items-center gap-2 py-2"
														>
															<Check className={cn("w-3.5 h-3.5 shrink-0", isSelected ? "opacity-100 text-coral" : "opacity-0")} />
															<span className="flex-1 truncate text-sm">{c.name}</span>
															<span className={cn("shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold", intentBg)}>
																{INTENT_LABELS[c.primary_intent]}
															</span>
															<span className={cn("shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold border", FRUIT_TIER_TOKEN_COLORS[c.fruit_tier as FruitTier] ?? FRUIT_TIER_TOKEN_COLORS.high)}>
																{FRUIT_TIER_LABELS[c.fruit_tier as FruitTier] ?? c.fruit_tier}
															</span>
															<span className="shrink-0 text-xs text-muted-foreground w-20 text-right">
																{(c.total_volume ?? 0).toLocaleString()} vol
															</span>
															<span className={cn("shrink-0 text-xs font-medium w-16", difficultyColor(c.avg_difficulty))}>
																{DIFFICULTY_LABELS[diffLevel]}
															</span>
														</CommandItem>
													);
												})}
											</CommandGroup>
										</>
									))}
									{mapGroups.length === 0 && !loadingClusters && (
										<div className="p-4 text-center space-y-2">
											<p className="text-xs text-muted-foreground">No completed keyword clusters found.</p>
											<Button size="sm" variant="outline" onClick={() => { setComboOpen(false); navigate("/seo/keyword-research"); }} className="h-7 text-xs">
												<ExternalLink className="w-3 h-3 mr-1" />Go to Keyword Research
											</Button>
										</div>
									)}
								</CommandList>
								{selectedIds.size > 0 && (
									<div className="border-t px-3 py-2 flex items-center justify-between">
										<span className="text-xs text-muted-foreground">{selectedIds.size} cluster{selectedIds.size !== 1 ? "s" : ""} selected</span>
										<button onClick={() => setSelectedIds(new Set())} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
									</div>
								)}
							</Command>
						</PopoverContent>
					</Popover>
				</div>

				{/* Location + custom keyword + search */}
				<div className="flex gap-2 items-center">
					{/* Market / location filter */}
					<div className="flex flex-col gap-0.5 w-48 shrink-0">
						<div className="relative">
							<MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
							<Input
								placeholder="Location (e.g. Houston, TX)"
								value={searchLocation}
								onChange={(e) => { setSearchLocation(e.target.value); setLocationFromBrandBible(false); }}
								onKeyDown={(e) => e.key === "Enter" && handleSearch()}
								className="h-9 text-sm pl-8"
							/>
							{searchLocation && (
								<button
									onClick={() => { setSearchLocation(""); setLocationFromBrandBible(false); }}
									className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
								>
									<X className="w-3.5 h-3.5" />
								</button>
							)}
						</div>
						{locationFromBrandBible && (
							<span className="text-[10px] text-teal-dark flex items-center gap-0.5 pl-0.5">
								<MapPin className="w-2.5 h-2.5" />from Brand Bible
							</span>
						)}
					</div>
					{/* Custom keyword override */}
					<div className="relative flex-1">
						<Input
							placeholder="Or type any keyword to override…"
							value={customKeyword}
							onChange={(e) => setCustomKeyword(e.target.value)}
							onKeyDown={(e) => e.key === "Enter" && handleSearch()}
							className="h-9 text-sm"
						/>
						{customKeyword && (
							<button
								onClick={() => setCustomKeyword("")}
								className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							>
								<X className="w-3.5 h-3.5" />
							</button>
						)}
					</div>
					<Button
						onClick={handleSearch}
						disabled={!hasSelection || isSearching}
						className="h-9 px-4 shrink-0 bg-coral hover:bg-coral-hover text-white"
					>
						{isSearching
							? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Searching…</>
							: <><Search className="w-3.5 h-3.5 mr-1.5" />Find Links</>
						}
					</Button>
				</div>
				{isSearching && searchProgress && (
					<div className="space-y-1">
						<div className="flex items-center justify-between text-[10px] text-muted-foreground">
							<span>Analyzing SERP results…</span>
							<span>{searchProgress.done}/{searchProgress.total} keywords</span>
						</div>
						<Progress
							value={Math.round((searchProgress.done / searchProgress.total) * 100)}
							className="h-1.5"
						/>
					</div>
				)}

				{/* SERP results — two-column graded output */}
				{(neverSuggestions.length > 0 || doSuggestions.length > 0) && (
					<div className="border-t pt-4 space-y-4">
					<div className="flex items-center justify-between gap-3 flex-wrap">
						<div className="flex items-center gap-2 flex-wrap">
							<p className="text-xs text-muted-foreground">
								Found{" "}
								<span className="font-medium text-destructive">{neverSuggestions.length} competitor{neverSuggestions.length !== 1 ? "s" : ""}</span>
								{", "}
								<span className="font-medium text-teal-dark">{doSuggestions.length} authority domain{doSuggestions.length !== 1 ? "s" : ""}</span>
								{", and "}
								<span className="font-medium text-coral">{allTopics.filter((t) => !internalLinks.some((item) => topicMatchesContent(t, item))).length} content gap{allTopics.filter((t) => !internalLinks.some((item) => topicMatchesContent(t, item))).length !== 1 ? "s" : ""}</span>
								{" "}across {customKw ? "1 keyword" : `${selectedIds.size} cluster${selectedIds.size !== 1 ? "s" : ""}`}.
							</p>
							{lastSearchLocation && (
								<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-light text-teal-dark text-[10px] font-medium border border-teal/20">
									<MapPin className="w-2.5 h-2.5" />
									{lastSearchLocation}
								</span>
							)}
						</div>
						<div className="flex items-center gap-1.5 shrink-0">
							{neverSuggestions.length > 0 && (
								<Button
									size="sm" variant="outline"
									onClick={() => handleBulkAdd("never")}
									disabled={isBulkAdding !== null}
									className="h-6 px-2 text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
								>
									{isBulkAdding === "never" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldX className="w-3 h-3 mr-1" />}
									Block All
								</Button>
							)}
							{doSuggestions.length > 0 && (
								<Button
									size="sm" variant="outline"
									onClick={() => handleBulkAdd("do")}
									disabled={isBulkAdding !== null}
									className="h-6 px-2 text-xs border-teal/50 text-teal-dark hover:bg-teal-light"
								>
									{isBulkAdding === "do" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
									Add All
								</Button>
							)}
							{neverSuggestions.length > 0 && doSuggestions.length > 0 && (
								<Button
									size="sm" variant="outline"
									onClick={() => handleBulkAdd("both")}
									disabled={isBulkAdding !== null}
									className="h-6 px-2 text-xs border-muted-foreground/30 text-muted-foreground hover:bg-muted"
								>
									{isBulkAdding === "both" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
									Block & Add All
								</Button>
							)}
						</div>
					</div>

						<div className="grid grid-cols-3 gap-4">
							{/* Never Use column */}
							<div className="space-y-2">
								<div className="flex items-center justify-between gap-1.5">
									<div className="flex items-center gap-1.5">
										<ShieldX className="w-3.5 h-3.5 text-destructive" />
										<span className="text-xs font-semibold text-destructive">Never Use ({hideAdded ? neverSuggestions.filter((s) => !neverEntries.entries.some((e) => e.domain === s.domain) && !doEntries.entries.some((e) => e.domain === s.domain)).length : neverSuggestions.length})</span>
									</div>
									<button onClick={() => setHideAdded((v) => !v)} className="text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0">{hideAdded ? "Show all" : "Hide added"}</button>
								</div>
								<ScrollArea className="h-72 pr-1"><div className="space-y-1.5">
									{neverSuggestions.length === 0 ? (
										<p className="text-xs text-muted-foreground py-3 text-center">No competitor domains found.</p>
									) : neverSuggestions.map((s) => {
										const inNever = neverEntries.entries.some((e) => e.domain === s.domain);
										const inDo = doEntries.entries.some((e) => e.domain === s.domain);
										const alreadyIn = inNever || inDo;
										if (hideAdded && alreadyIn) return null;
										const addKey = s.domain + "never_use";
										return (
											<div key={s.domain} className="rounded-md border bg-background px-3 py-2 space-y-1.5">
												<div className="flex items-center gap-2">
													<img
														src={`https://www.google.com/s2/favicons?domain=${s.domain}&sz=16`}
														alt=""
														className="w-4 h-4 rounded shrink-0"
														onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
													/>
													<span className={cn("flex-1 text-sm font-medium truncate", alreadyIn && "text-muted-foreground line-through")}>{s.domain}</span>
													{alreadyIn ? (
														<Badge variant="secondary" className="text-xs shrink-0">{inNever ? "Added" : "In Do Use"}</Badge>
													) : (
														<Button
															size="sm" variant="outline"
															onClick={() => handleAdd(s.domain, s.title, "never_use")}
															disabled={!!addingMap[addKey]}
															className="h-6 px-2 text-xs border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0"
														>
															{addingMap[addKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ShieldX className="w-3 h-3 mr-0.5" />Block</>}
														</Button>
													)}
												</div>
												<div className="flex items-center gap-2">
													<span className={cn("px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0", THREAT_COLORS[s.threat_level])}>
														{s.threat_level}
													</span>
													<div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
														<div
															className={cn("h-full rounded-full", s.threat_level === "High" ? "bg-destructive" : s.threat_level === "Medium" ? "bg-coral" : "bg-muted-foreground/40")}
															style={{ width: `${s.threat_score}%` }}
														/>
													</div>
													<span className="text-[10px] text-muted-foreground shrink-0">{s.threat_score}/100</span>
												</div>
												<p className="text-[10px] text-muted-foreground leading-relaxed">{s.reasons.join(" · ")}</p>
											</div>
										);
									})}
								</div></ScrollArea>
							</div>

							{/* Do Use column */}
							<div className="space-y-2">
								<div className="flex items-center justify-between gap-1.5">
									<div className="flex items-center gap-1.5">
										<ShieldCheck className="w-3.5 h-3.5 text-teal-dark" />
										<span className="text-xs font-semibold text-teal-dark">Do Use ({hideAdded ? doSuggestions.filter((s) => !neverEntries.entries.some((e) => e.domain === s.domain) && !doEntries.entries.some((e) => e.domain === s.domain)).length : doSuggestions.length})</span>
									</div>
								</div>
									<button onClick={() => setHideAdded((v) => !v)} className="text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0">{hideAdded ? "Show all" : "Hide added"}</button>
								<ScrollArea className="h-72 pr-1"><div className="space-y-1.5">
									{doSuggestions.length === 0 ? (
										<p className="text-xs text-muted-foreground py-3 text-center">No authority domains found.</p>
									) : doSuggestions.map((s) => {
										const inNever = neverEntries.entries.some((e) => e.domain === s.domain);
										const inDo = doEntries.entries.some((e) => e.domain === s.domain);
										const alreadyIn = inNever || inDo;
										if (hideAdded && alreadyIn) return null;
										const addKey = s.domain + "do_use";
										return (
											<div key={s.domain} className="rounded-md border bg-background px-3 py-2 space-y-1.5">
												<div className="flex items-center gap-2">
													<img
														src={`https://www.google.com/s2/favicons?domain=${s.domain}&sz=16`}
														alt=""
														className="w-4 h-4 rounded shrink-0"
														onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
													/>
													<span className={cn("flex-1 text-sm font-medium truncate", alreadyIn && "text-muted-foreground line-through")}>{s.domain}</span>
													{alreadyIn ? (
														<Badge variant="secondary" className="text-xs shrink-0">{inDo ? "Added" : "In Never Use"}</Badge>
													) : (
														<Button
															size="sm" variant="outline"
															onClick={() => handleAdd(s.domain, s.title, "do_use")}
															disabled={!!addingMap[addKey]}
															className="h-6 px-2 text-xs border-teal/50 text-teal-dark hover:bg-teal-light shrink-0"
														>
															{addingMap[addKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ShieldCheck className="w-3 h-3 mr-0.5" />Add</>}
														</Button>
													)}
												</div>
												<div className="flex items-center gap-2">
													<span className={cn("px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0", TIER_COLORS[s.authority_tier])}>
														{TIER_LABELS[s.authority_tier]}
													</span>
													<div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
														<div
															className="h-full rounded-full bg-teal"
															style={{ width: `${s.authority_score}%` }}
														/>
													</div>
													<span className="text-[10px] text-muted-foreground shrink-0">{s.authority_score}/100</span>
												</div>
												<p className="text-[10px] text-muted-foreground leading-relaxed">{s.reasons.join(" · ")}</p>
											</div>
										);
									})}
								</div></ScrollArea>
							</div>

							{/* Your Site column */}
							<div className="space-y-2">
								<div className="flex items-center justify-between gap-1.5">
									<div className="flex items-center gap-1.5">
										<Link2 className="w-3.5 h-3.5 text-primary" />
										<span className="text-xs font-semibold text-primary">Your Site</span>
									</div>
									{allTopics.filter((t) => !savedGapKeywords.has(t) && !internalLinks.some((item) => topicMatchesContent(t, item))).length > 0 && (
										<Button
											size="sm" variant="outline"
											onClick={handleSaveAllGaps}
											disabled={isSavingAllGaps}
											className="h-6 px-2 text-[10px] border-coral/40 text-coral hover:bg-coral-light"
										>
											{isSavingAllGaps ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
											Save All Gaps
										</Button>
									)}
								</div>
								<ScrollArea className="h-72 pr-1"><div className="space-y-1.5">
									<YourSiteRows
										allTopics={allTopics}
										internalLinks={internalLinks}
										savedGaps={savedGapKeywords}
										onSaveGap={handleSaveGap}
									/>
								</div></ScrollArea>
							</div>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Link Policy Tab
// ---------------------------------------------------------------------------

function LinkPolicyTab({ tenantId, onNavigateToGaps }: { tenantId: string | null; onNavigateToGaps?: () => void }) {
	const addNever = useAddToPolicy();
	const addDo = useAddToPolicy();

	// Pending external add from research panel → routed to the right list
	const [pendingNever, setPendingNever] = useState<{ domain: string; title: string } | null>(null);
	const [pendingDo, setPendingDo] = useState<{ domain: string; title: string } | null>(null);

	if (!tenantId) {
		return <div className="text-center py-12 text-muted-foreground">No workspace selected.</div>;
	}

	const handleResearchAdd = async (domain: string, title: string, policyType: "never_use" | "do_use") => {
		try {
			const mutation = policyType === "never_use" ? addNever : addDo;
			await mutation.mutateAsync({ tenantId, domain, policyType, name: title });
			toast.success(`Added "${domain}" to ${policyType === "never_use" ? "Never Use" : "Do Use"}`);
		} catch (err: any) {
			toast.error(err?.response?.data?.detail || `Failed to add "${domain}"`);
			throw err; // re-throw so ResearchPanel can clear its loading state
		}
	};

	return (
		<div className="space-y-6">
			<ResearchPanel tenantId={tenantId} onAdd={handleResearchAdd} onNavigateToGaps={onNavigateToGaps} />

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<Card className="border shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-base">
							<ShieldX className="w-4 h-4 text-destructive" />
							Never Use
						</CardTitle>
						<p className="text-xs text-muted-foreground">Blocked from all generated links.</p>
					</CardHeader>
					<CardContent>
						<PolicyListPanel
							tenantId={tenantId}
							policyType="never_use"
							externalDomainToAdd={pendingNever}
							onExternalAdded={() => setPendingNever(null)}
						/>
					</CardContent>
				</Card>

				<Card className="border shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-base">
							<ShieldCheck className="w-4 h-4 text-teal" />
							Do Use
						</CardTitle>
						<p className="text-xs text-muted-foreground">Preferred domains for external links.</p>
					</CardHeader>
					<CardContent>
						<PolicyListPanel
							tenantId={tenantId}
							policyType="do_use"
							externalDomainToAdd={pendingDo}
							onExternalAdded={() => setPendingDo(null)}
						/>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function CompetitorComparisonPage() {
	const { currentTenant } = useTenantStore();
	const [activeTab, setActiveTab] = useState("url-compare");
	const [urls, setUrls] = useState<string[]>(["", ""]);
	const [results, setResults] = useState<CompareUrlResult[] | null>(null);

	const compareMutation = useMutation({
		mutationFn: async (urlList: string[]) => {
			const { data } = await apiClient.post<CompareResponse>(
				"/seo-tools/compare",
				{ urls: urlList },
			);
			return data;
		},
		onSuccess: (data) => {
			setResults(data.results);
			toast.success(`Compared ${data.results.length} URLs`);
		},
		onError: (err: any) => {
			const detail =
				err?.response?.data?.detail || err?.message || "Comparison failed";
			toast.error(detail);
		},
	});

	const handleAddUrl = () => {
		if (urls.length < 5) setUrls([...urls, ""]);
	};

	const handleRemoveUrl = (index: number) => {
		if (urls.length > 2) setUrls(urls.filter((_, i) => i !== index));
	};

	const handleUrlChange = (index: number, value: string) => {
		const updated = [...urls];
		updated[index] = value;
		setUrls(updated);
	};

	const handleCompare = () => {
		const filledUrls = urls.map((u) => u.trim()).filter(Boolean);
		if (filledUrls.length < 2) {
			toast.error("Enter at least 2 URLs to compare");
			return;
		}
		compareMutation.mutate(filledUrls);
	};

	const isLoading = compareMutation.isPending;
	const hasResults = results && results.length > 0;

	return (
		<div className="w-full space-y-6">
			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<TabsList className={`grid w-full mb-6 ${FEATURE_FLAGS.competitorV2 ? 'grid-cols-4' : 'grid-cols-3'}`}>
					{FEATURE_FLAGS.competitorV2 && (
						<TabsTrigger value="top-pages" className="gap-1.5">
							<TrendingUp className="w-4 h-4" />
							Top Pages
						</TabsTrigger>
					)}
					<TabsTrigger value="url-compare" className="gap-1.5">
						<Scale className="w-4 h-4" />
						URL Comparison
					</TabsTrigger>
					<TabsTrigger value="link-policy" className="gap-1.5">
						<ShieldX className="w-4 h-4" />
						Link Policy
					</TabsTrigger>
					<TabsTrigger value="saved-gaps" className="gap-1.5 relative">
						<Bookmark className="w-4 h-4" />
						Saved Gaps
						<SavedGapsBadge tenantId={currentTenant} />
					</TabsTrigger>
				</TabsList>

				{FEATURE_FLAGS.competitorV2 && (
					<TabsContent value="top-pages">
						<CompetitorTopPages tenantId={currentTenant} />
					</TabsContent>
				)}

				{/* ------------------------------------------------------------------ */}
				{/* URL Comparison Tab                                                   */}
				{/* ------------------------------------------------------------------ */}
				<TabsContent value="url-compare">
					<div className="space-y-6">
						{/* Input Card */}
						<Card className="border shadow-sm">
							<CardContent className="p-6 sm:p-8">
								<div className="flex items-start gap-4 mb-8">
									<div className="w-12 h-12 rounded-xl bg-coral-light flex items-center justify-center flex-shrink-0">
										<Scale className="w-6 h-6 text-coral" />
									</div>
									<div>
										<h1 className="text-2xl font-bold text-foreground tracking-tight">
											Competitor Comparison
										</h1>
										<p className="text-muted-foreground mt-1">
											Compare SEO scores of up to 5 URLs side-by-side.
										</p>
									</div>
								</div>

								<div className="space-y-3">
									{urls.map((url, index) => (
										<div key={index} className="flex gap-2">
											<Input
												placeholder={`URL ${index + 1}${index < 2 ? " (required)" : ""}...`}
												value={url}
												onChange={(e) => handleUrlChange(index, e.target.value)}
												onKeyDown={(e) => e.key === "Enter" && handleCompare()}
												disabled={isLoading}
												className="h-12 text-base"
											/>
											{index >= 2 && (
												<Button
													variant="ghost"
													size="icon"
													onClick={() => handleRemoveUrl(index)}
													disabled={isLoading}
													className="h-12 w-12"
												>
													<X className="w-4 h-4" />
												</Button>
											)}
										</div>
									))}
								</div>

								{urls.length < 5 && (
									<Button
										variant="outline"
										onClick={handleAddUrl}
										disabled={isLoading}
										className="w-full mt-3 h-12 border-dashed border-2 hover:border-coral/40 hover:bg-coral-light/50 transition-colors"
									>
										<Plus className="w-4 h-4 mr-2" />
										<span>Add Competitor</span>
										<span className="ml-1 text-coral font-medium">({urls.length}/5)</span>
									</Button>
								)}

								<Button
									onClick={handleCompare}
									disabled={isLoading}
									className="w-full mt-4 h-12 bg-coral hover:bg-coral-hover text-white font-semibold text-base"
								>
									{isLoading ? (
										<>
											<Loader2 className="w-4 h-4 animate-spin mr-2" />
											Analyzing… (this may take a minute)
										</>
									) : (
										"Compare"
									)}
								</Button>
							</CardContent>
						</Card>

						{/* Empty state */}
						{!hasResults && (
							<div className="bg-muted/50 rounded-xl p-6 border border-border">
								<div className="flex items-center gap-2 mb-4">
									<BarChart3 className="w-4 h-4 text-coral" />
									<h3 className="font-semibold text-foreground text-sm">Perfect for</h3>
								</div>
								<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
									<PerfectForCard
										icon={CheckCircle}
										title="Competitor Analysis"
										description="Understand competitor strategies"
										iconColor="text-teal"
									/>
									<PerfectForCard
										icon={TrendingUp}
										title="Benchmarking"
										description="Compare against industry leaders"
										iconColor="text-teal-dark"
									/>
									<PerfectForCard
										icon={Search}
										title="Find Gaps"
										description="Identify optimization opportunities"
										iconColor="text-coral"
									/>
								</div>
							</div>
						)}

						{/* Results */}
						{hasResults && (
							<div className="space-y-6">
								{/* URL Headers */}
								<div
									className="grid items-center gap-3"
									style={{ gridTemplateColumns: `140px repeat(${results.length}, 1fr)` }}
								>
									<div />
									{results.map((r) => (
										<div key={r.url} className="text-center space-y-1">
											<div className="flex items-center justify-center gap-1.5">
												{r.meta.favicon && (
													<img
														src={r.meta.favicon}
														alt=""
														className="w-4 h-4 rounded-full"
														onError={(e) => {
															(e.target as HTMLImageElement).style.display = "none";
														}}
													/>
												)}
												<span className="text-sm font-semibold truncate max-w-[180px]">
													{getDomain(r.url)}
												</span>
											</div>
											{r.error && (
												<Badge variant="destructive" className="text-xs">
													<AlertTriangle className="w-3 h-3 mr-1" />
													Partial data
												</Badge>
											)}
										</div>
									))}
								</div>

								{(() => {
							const scores = results.map((r) =>
								(r.pagespeed_mobile.performance ?? 0) +
								(r.pagespeed_mobile.seo ?? 0) +
								(r.pagespeed_mobile.accessibility ?? 0) +
								(r.pagespeed_desktop.performance ?? 0)
							);
							const maxScore = Math.max(...scores);
							const winnerIdx = scores.indexOf(maxScore);
							const allTied = scores.every((s) => s === maxScore);
							if (allTied || maxScore === 0) return null;
							return (
								<div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-light border border-teal/20">
									<CheckCircle2 className="w-4 h-4 text-teal-dark shrink-0" />
									<span className="text-sm text-teal-dark">
										<span className="font-semibold">{getDomain(results[winnerIdx].url)}</span>
										{" "}leads on overall PageSpeed performance
									</span>
								</div>
							);
						})()}
						<Tabs defaultValue="performance" className="w-full">
									<TabsList className="grid w-full grid-cols-4">
										<TabsTrigger value="performance" className="text-xs gap-1">
											<Globe className="w-3.5 h-3.5" />
											Performance
										</TabsTrigger>
										<TabsTrigger value="meta" className="text-xs gap-1">
											<FileText className="w-3.5 h-3.5" />
											Meta Tags
										</TabsTrigger>
										<TabsTrigger value="headings" className="text-xs gap-1">
											<Heading1 className="w-3.5 h-3.5" />
											Headings
										</TabsTrigger>
										<TabsTrigger value="images" className="text-xs gap-1">
											<Image className="w-3.5 h-3.5" />
											Images
										</TabsTrigger>
									</TabsList>

									{/* Performance Tab */}
									<TabsContent value="performance" className="mt-4">
										<Accordion type="multiple" defaultValue={["mobile", "desktop"]} className="space-y-2">
											<AccordionItem value="mobile" className="border rounded-lg">
												<AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
													Mobile Scores
												</AccordionTrigger>
												<AccordionContent className="px-4 pb-4">
													<div className="grid gap-3" style={{ gridTemplateColumns: `140px repeat(${results.length}, 1fr)` }}>
														<div />
														{results.map((r) => (
															<div key={r.url} className="flex justify-center gap-2 flex-wrap">
																<ScoreCircle score={r.pagespeed_mobile.performance} label="Perf" />
																<ScoreCircle score={r.pagespeed_mobile.seo} label="SEO" />
																<ScoreCircle score={r.pagespeed_mobile.accessibility} label="A11y" />
															</div>
														))}
													</div>
													<div className="mt-4 space-y-2 border-t pt-4">
														<MetricRow label="FCP (ms)" values={results.map((r) => r.pagespeed_mobile.fcp)} format={(v) => `${v}ms`} higherIsBetter={false} />
														<MetricRow label="LCP (ms)" values={results.map((r) => r.pagespeed_mobile.lcp)} format={(v) => `${v}ms`} higherIsBetter={false} />
														<MetricRow label="CLS" values={results.map((r) => r.pagespeed_mobile.cls)} format={(v) => v.toFixed(3)} higherIsBetter={false} />
														<MetricRow label="TBT (ms)" values={results.map((r) => r.pagespeed_mobile.tbt)} format={(v) => `${v}ms`} higherIsBetter={false} />
													</div>
												</AccordionContent>
											</AccordionItem>

											<AccordionItem value="desktop" className="border rounded-lg">
												<AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
													Desktop Scores
												</AccordionTrigger>
												<AccordionContent className="px-4 pb-4">
													<div className="grid gap-3" style={{ gridTemplateColumns: `140px repeat(${results.length}, 1fr)` }}>
														<div />
														{results.map((r) => (
															<div key={r.url} className="flex justify-center gap-2 flex-wrap">
																<ScoreCircle score={r.pagespeed_desktop.performance} label="Perf" />
																<ScoreCircle score={r.pagespeed_desktop.seo} label="SEO" />
																<ScoreCircle score={r.pagespeed_desktop.accessibility} label="A11y" />
															</div>
														))}
													</div>
													<div className="mt-4 space-y-2 border-t pt-4">
														<MetricRow label="FCP (ms)" values={results.map((r) => r.pagespeed_desktop.fcp)} format={(v) => `${v}ms`} higherIsBetter={false} />
														<MetricRow label="LCP (ms)" values={results.map((r) => r.pagespeed_desktop.lcp)} format={(v) => `${v}ms`} higherIsBetter={false} />
														<MetricRow label="CLS" values={results.map((r) => r.pagespeed_desktop.cls)} format={(v) => v.toFixed(3)} higherIsBetter={false} />
														<MetricRow label="TBT (ms)" values={results.map((r) => r.pagespeed_desktop.tbt)} format={(v) => `${v}ms`} higherIsBetter={false} />
													</div>
												</AccordionContent>
											</AccordionItem>
										</Accordion>
									</TabsContent>

									{/* Meta Tags Tab */}
									<TabsContent value="meta" className="mt-4">
										<Card>
											<CardContent className="pt-6 space-y-3">
												<MetricRow label="Title Length" values={results.map((r) => r.meta.title_length)} format={(v) => `${v} chars`} />
												<MetricRow label="Description Length" values={results.map((r) => r.meta.meta_length)} format={(v) => `${v} chars`} />
												<BooleanRow label="Has OG Tags" values={results.map((r) => r.meta.has_og_tags)} />
												<BooleanRow label="Has Twitter Tags" values={results.map((r) => r.meta.has_twitter_tags)} />
												<div className="border-t pt-3 mt-3 space-y-3">
													{results.map((r) => (
														<div key={r.url} className="text-xs space-y-1">
															<span className="font-medium">{getDomain(r.url)}</span>
															<p className="text-muted-foreground truncate" title={r.meta.title || ""}>
																Title: {r.meta.title || <span className="text-destructive">Missing</span>}
															</p>
															<p className="text-muted-foreground truncate" title={r.meta.meta_description || ""}>
																Desc: {r.meta.meta_description || <span className="text-destructive">Missing</span>}
															</p>
														</div>
													))}
												</div>
											</CardContent>
										</Card>
									</TabsContent>

									{/* Headings Tab */}
									<TabsContent value="headings" className="mt-4">
										<Card>
											<CardContent className="pt-6 space-y-3">
												<BooleanRow label="Has H1" values={results.map((r) => r.headings.has_h1)} />
												<MetricRow label="H1 Count" values={results.map((r) => r.headings.h1_count)} />
												<div className="border-t pt-3 mt-3 space-y-3">
													{results.map((r) => (
														<div key={r.url} className="text-xs space-y-1">
															<span className="font-medium">{getDomain(r.url)}</span>
															{r.headings.h1_texts.length > 0 ? (
																r.headings.h1_texts.map((h1, i) => (
																	<p key={i} className="text-muted-foreground truncate pl-2">
																		H1: &quot;{h1}&quot;
																	</p>
																))
															) : (
																<p className="text-destructive pl-2">No H1 found</p>
															)}
														</div>
													))}
												</div>
											</CardContent>
										</Card>
									</TabsContent>

									{/* Images Tab */}
									<TabsContent value="images" className="mt-4">
										<Card>
											<CardContent className="pt-6 space-y-3">
												<MetricRow label="Total Images" values={results.map((r) => r.total_images)} />
												<MetricRow label="With Alt Text" values={results.map((r) => r.images_with_alt)} />
												<MetricRow label="Alt Coverage" values={results.map((r) => r.image_alt_coverage)} format={(v) => `${v}%`} />
											</CardContent>
										</Card>
									</TabsContent>
								</Tabs>
							</div>
						)}
					</div>
				</TabsContent>

				{/* ------------------------------------------------------------------ */}
				{/* Link Policy Tab                                                      */}
				{/* ------------------------------------------------------------------ */}
				<TabsContent value="link-policy">
					<LinkPolicyTab tenantId={currentTenant} onNavigateToGaps={() => setActiveTab("saved-gaps")} />
				</TabsContent>

				{/* ------------------------------------------------------------------ */}
				{/* Saved Gaps Tab                                                       */}
				{/* ------------------------------------------------------------------ */}
				<TabsContent value="saved-gaps">
					<SavedGapsTab tenantId={currentTenant} />
				</TabsContent>
			</Tabs>
		</div>
	);
}

// ---------------------------------------------------------------------------
// SavedGapsBadge — live count dot shown on the tab
// ---------------------------------------------------------------------------
function SavedGapsBadge({ tenantId }: { tenantId: string | null }) {
	const { data: gaps } = useSavedGaps(tenantId);
	if (!gaps || gaps.length === 0) return null;
	return (
		<span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-coral text-white text-[10px] font-semibold flex items-center justify-center px-1">
			{gaps.length}
		</span>
	);
}

// ---------------------------------------------------------------------------
// SavedGapsTab helpers
// ---------------------------------------------------------------------------
type OpportunityLevel = "high" | "medium" | "low" | "unknown";

function getOpportunityLevel(gap: SavedGap): OpportunityLevel {
	if (!gap.enriched) return "unknown";
	const { volume, kd } = gap;
	if (kd < 30 && volume >= 500) return "high";
	if (kd < 50 && volume >= 100) return "medium";
	return "low";
}

const OPPORTUNITY_CONFIG: Record<OpportunityLevel, { label: string; className: string }> = {
	high:    { label: "High",    className: "bg-teal-light text-teal-dark border border-teal/30" },
	medium:  { label: "Medium",  className: "bg-coral-light text-coral-dark border border-coral/30" },
	low:     { label: "Low",     className: "bg-muted text-muted-foreground border" },
	unknown: { label: "—",       className: "bg-muted/50 text-muted-foreground border" },
};

function OpportunityBadge({ gap }: { gap: SavedGap }) {
	const level = getOpportunityLevel(gap);
	const { label, className } = OPPORTUNITY_CONFIG[level];
	return (
		<span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${className}`}>
			{label}
		</span>
	);
}

function formatVolume(v: number): string {
	if (v === 0) return "—";
	if (v >= 10000) return `${(v / 1000).toFixed(0)}k`;
	if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
	return String(v);
}

const CONTENT_STATUS_CONFIG: Record<GapContentStatus, { label: string; icon: React.ReactNode; className: string }> = {
	not_started: { label: "Not Started", icon: <CircleDot className="w-3 h-3" />, className: "text-muted-foreground" },
	in_progress: { label: "In Progress", icon: <Loader2 className="w-3 h-3 animate-spin" />, className: "text-teal-dark" },
	draft:       { label: "Draft",       icon: <FileEdit className="w-3 h-3" />, className: "text-coral-dark" },
	published:   { label: "Published",   icon: <CheckCircle className="w-3 h-3" />, className: "text-teal-dark" },
	failed:      { label: "Failed",      icon: <XCircle className="w-3 h-3" />, className: "text-destructive" },
};

function ContentStatusCell({ gap }: { gap: SavedGap }) {
	const cfg = CONTENT_STATUS_CONFIG[gap.content_status];
	if (gap.content_status === "not_started") {
		return <span className="text-xs text-muted-foreground">—</span>;
	}
	const inner = (
		<span className={`inline-flex items-center gap-1 text-xs font-medium ${cfg.className}`}>
			{cfg.icon}
			{cfg.label}
		</span>
	);
	if (gap.content_status === "published" && gap.content_url) {
		return (
			<a href={gap.content_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
				{inner}
			</a>
		);
	}
	return inner;
}

// ---------------------------------------------------------------------------
// SavedGapsTab
// ---------------------------------------------------------------------------
function SavedGapsTab({ tenantId }: { tenantId: string | null }) {
	const navigate = useNavigate();
	const { data: gaps, isLoading } = useSavedGaps(tenantId);
	const deleteGap = useDeleteGap(tenantId);
	const enrichMutation = useEnrichGaps(tenantId);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);

	const unenrichedCount = gaps?.filter((g) => !g.enriched).length ?? 0;
	const allIds = gaps?.map((g) => g.id) ?? [];
	const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
	const someSelected = selectedIds.size > 0;

	const toggleSelect = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const toggleSelectAll = () => {
		setSelectedIds(allSelected ? new Set() : new Set(allIds));
	};

	const handleDelete = async (gap: SavedGap) => {
		setDeletingId(gap.id);
		try {
			await deleteGap(gap.id);
			setSelectedIds((prev) => { const n = new Set(prev); n.delete(gap.id); return n; });
			toast.success(`"${gap.topic}" removed from Saved Gaps`);
		} catch {
			toast.error("Failed to remove gap");
		} finally {
			setDeletingId(null);
		}
	};

	const handleBulkDelete = async () => {
		if (!window.confirm(`Delete ${selectedIds.size} saved gap${selectedIds.size !== 1 ? "s" : ""}?`)) return;
		setIsBulkDeleting(true);
		let ok = 0; let fail = 0;
		for (const id of Array.from(selectedIds)) {
			try { await deleteGap(id); ok++; } catch { fail++; }
		}
		setSelectedIds(new Set());
		setIsBulkDeleting(false);
		if (ok > 0) toast.success(`Deleted ${ok} gap${ok !== 1 ? "s" : ""}`);
		if (fail > 0) toast.error(`${fail} deletion${fail !== 1 ? "s" : ""} failed`);
	};

	const handleBulkCreate = () => {
		const selected = (gaps ?? []).filter((g) => selectedIds.has(g.id));
		if (selected.length === 0) return;
		// Navigate to first; open remaining in new tabs
		selected.forEach((gap, i) => {
			const url = `/content/planner?keyword=${encodeURIComponent(gap.topic)}&open=true`;
			if (i === 0) navigate(url);
			else window.open(url, "_blank");
		});
	};

	const handleCreate = (gap: SavedGap) => {
		navigate(`/content/planner?keyword=${encodeURIComponent(gap.topic)}&open=true`);
	};

	const handleEnrich = async (force = false) => {
		try {
			const result = await enrichMutation.mutateAsync({ force });
			if (result.enriched > 0) {
				toast.success(`Enriched ${result.enriched} keyword${result.enriched !== 1 ? "s" : ""} with Ahrefs metrics`);
			} else {
				toast.info(force ? "All gaps re-enriched (no updates)" : "All gaps already enriched");
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.detail || "Failed to fetch Ahrefs metrics");
		}
	};

	return (
		<Card className="border shadow-sm">
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between gap-4">
					<div className="flex items-center gap-3">
						<div className="w-9 h-9 rounded-xl bg-teal-light flex items-center justify-center flex-shrink-0">
							<Bookmark className="w-4 h-4 text-teal-dark" />
						</div>
						<div>
							<CardTitle className="text-base">Saved Gaps</CardTitle>
							<p className="text-xs text-muted-foreground mt-0.5">
								Content opportunities from competitor analysis
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						{gaps && gaps.length > 0 && (
							<span className="text-xs text-muted-foreground">{gaps.length} keyword{gaps.length !== 1 ? "s" : ""}</span>
						)}
						{gaps && gaps.length > 0 && (
							<Button
								size="sm"
								variant="ghost"
								className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
								onClick={() => handleEnrich(true)}
								disabled={enrichMutation.isPending}
								title="Re-fetch Ahrefs metrics for all gaps"
							>
								<RotateCcw className="w-3 h-3" />
								Re-enrich all
							</Button>
						)}
						{unenrichedCount > 0 && (
							<Button
								size="sm"
								variant="outline"
								className="h-7 px-3 text-xs gap-1.5"
								onClick={() => handleEnrich(false)}
								disabled={enrichMutation.isPending}
							>
								{enrichMutation.isPending
									? <Loader2 className="w-3 h-3 animate-spin" />
									: <span>✦</span>}
								Enrich {unenrichedCount} with Ahrefs
							</Button>
						)}
					</div>
				</div>
			</CardHeader>
			<CardContent className="p-0">
				{isLoading ? (
					<div className="p-6 space-y-2">
						{[1, 2, 3].map((i) => (
							<div key={i} className="h-10 rounded-lg bg-muted animate-pulse" />
						))}
					</div>
				) : !gaps || gaps.length === 0 ? (
					<div className="text-center py-12 text-muted-foreground">
						<Bookmark className="w-8 h-8 mx-auto mb-3 opacity-30" />
						<p className="text-sm font-medium">No gaps saved yet</p>
						<p className="text-xs mt-1">
							Use the <span className="font-medium">Find Links</span> tab to discover competitor topics and save them here.
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						{someSelected && (
							<div className="flex items-center gap-2 px-4 py-2 bg-teal-light border-b text-sm">
								<span className="text-teal-dark font-medium">{selectedIds.size} selected</span>
								<Button size="sm" variant="outline" className="h-7 px-3 text-xs border-teal/40 text-teal-dark hover:bg-teal/10" onClick={handleBulkCreate}>
									Create Content for {selectedIds.size} →
								</Button>
								<Button size="sm" variant="ghost" className="h-7 px-3 text-xs text-destructive hover:bg-destructive/10" onClick={handleBulkDelete} disabled={isBulkDeleting}>
									{isBulkDeleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
									Delete {selectedIds.size}
								</Button>
								<Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground ml-auto" onClick={() => setSelectedIds(new Set())}>
									Clear
								</Button>
							</div>
						)}
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b bg-muted/40">
									<th className="px-3 py-2 w-8">
										<button onClick={toggleSelectAll} className="flex items-center justify-center text-muted-foreground hover:text-foreground">
											{allSelected ? <CheckSquare className="w-4 h-4 text-teal-dark" /> : <Square className="w-4 h-4" />}
										</button>
									</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2 w-6">#</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2">Keyword / Topic</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2 w-20">Volume</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2 w-14">KD</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2 w-24">Opportunity</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2 w-24">Content</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2">Why</th>
									<th className="text-left text-xs font-medium text-muted-foreground px-3 py-2 w-24">Saved On</th>
									<th className="text-right text-xs font-medium text-muted-foreground px-3 py-2 w-32">Actions</th>
								</tr>
							</thead>
							<tbody>
								{gaps.map((gap, idx) => {
									const isSelected = selectedIds.has(gap.id);
									return (
									<tr
										key={gap.id}
										className={`border-b last:border-0 transition-colors group cursor-pointer ${isSelected ? "bg-teal-light/50" : "hover:bg-muted/20"}`}
										onClick={() => toggleSelect(gap.id)}
									>
										<td className="px-3 py-3">
											{isSelected ? <CheckSquare className="w-4 h-4 text-teal-dark" /> : <Square className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground" />}
										</td>
										<td className="px-3 py-3 text-xs text-muted-foreground tabular-nums">{idx + 1}</td>
										<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
											<span className="font-medium capitalize">{gap.topic}</span>
										</td>
										<td className="px-3 py-3 text-xs tabular-nums text-muted-foreground">
											{formatVolume(gap.volume)}
										</td>
										<td className="px-3 py-3 text-xs tabular-nums">
											{gap.enriched ? (
												<span className={`font-medium ${gap.kd < 30 ? "text-[hsl(var(--status-success))]" : gap.kd < 60 ? "text-amber-600 dark:text-amber-400" : "text-destructive"}`}>
													{gap.kd}
												</span>
											) : "—"}
										</td>
										<td className="px-3 py-3">
											<OpportunityBadge gap={gap} />
										</td>
										<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
											<ContentStatusCell gap={gap} />
										</td>
										<td className="px-3 py-3 text-xs text-muted-foreground max-w-[180px]">
											{gap.opportunity_reason
												? <span className="line-clamp-2">{gap.opportunity_reason}</span>
												: <span className="italic opacity-60">Enrich to see details</span>}
										</td>
										<td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
											{gap.created_at
												? new Date(gap.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })
												: "—"}
										</td>
										<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
											<div className="flex items-center justify-end gap-1">
												<Button
													size="sm"
													variant="outline"
													className="h-7 px-2 text-xs border-teal/40 text-teal-dark hover:bg-teal-light"
													onClick={() => handleCreate(gap)}
												>
													Create →
												</Button>
												<Button
													size="sm"
													variant="ghost"
													className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
													disabled={deletingId === gap.id}
													onClick={() => handleDelete(gap)}
													title="Remove gap"
												>
													{deletingId === gap.id
														? <Loader2 className="w-3.5 h-3.5 animate-spin" />
														: <X className="w-3.5 h-3.5" />}
												</Button>
											</div>
										</td>
									</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
